import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  strapiCreatePermissionAccess,
  strapiCreatePermissionGroup,
  strapiDeletePermissionAccess,
  strapiDeletePermissionGroup,
  strapiUpdatePermissionAccess,
  strapiUpdatePermissionGroup,
} from '../../api/strapiPermissionMaster';
import { queryKeys } from '../queryKeys';

async function invalidatePermissionData(qc: ReturnType<typeof useQueryClient>, jwt: string | null) {
  await qc.invalidateQueries({ queryKey: queryKeys.permissionMaster(jwt) });
  await qc.invalidateQueries({ queryKey: queryKeys.permissionAccessCatalog(jwt) });
  await qc.invalidateQueries({ queryKey: queryKeys.navigationMenu(jwt) });
}

export function useCreatePermissionGroupMutation(jwt: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (group_name: string) => {
      if (!jwt) throw new Error('Not signed in');
      return strapiCreatePermissionGroup(jwt, group_name);
    },
    onSuccess: async () => {
      await invalidatePermissionData(qc, jwt);
    },
  });
}

export function useUpdatePermissionGroupMutation(jwt: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, group_name }: { documentId: string; group_name: string }) => {
      if (!jwt) throw new Error('Not signed in');
      return strapiUpdatePermissionGroup(jwt, documentId, group_name);
    },
    onSuccess: async () => {
      await invalidatePermissionData(qc, jwt);
    },
  });
}

export function useDeletePermissionGroupMutation(jwt: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => {
      if (!jwt) throw new Error('Not signed in');
      return strapiDeletePermissionGroup(jwt, documentId);
    },
    onSuccess: async () => {
      await invalidatePermissionData(qc, jwt);
    },
  });
}

export function useCreatePermissionAccessMutation(jwt: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      permissionGroupDocumentId: string;
      permissionGroupNumericId?: number | null;
      access_code: string;
      access_name: string;
      description?: string;
      nav_path?: string;
    }) => {
      if (!jwt) throw new Error('Not signed in');
      return strapiCreatePermissionAccess(jwt, input);
    },
    onSuccess: async () => {
      await invalidatePermissionData(qc, jwt);
    },
  });
}

export function useUpdatePermissionAccessMutation(jwt: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentId,
      ...patch
    }: {
      documentId: string;
      access_code?: string;
      access_name?: string;
      description?: string;
      nav_path?: string;
      permissionGroupDocumentId?: string;
    }) => {
      if (!jwt) throw new Error('Not signed in');
      return strapiUpdatePermissionAccess(jwt, documentId, patch);
    },
    onSuccess: async () => {
      await invalidatePermissionData(qc, jwt);
    },
  });
}

export function useDeletePermissionAccessMutation(jwt: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => {
      if (!jwt) throw new Error('Not signed in');
      return strapiDeletePermissionAccess(jwt, documentId);
    },
    onSuccess: async () => {
      await invalidatePermissionData(qc, jwt);
    },
  });
}
